// @ts-nocheck

/**
 * Mock data for DataTableWidget examples
 */

// ============================================================================
// User Data
// ============================================================================

export interface User {
  id: string;
  name: string;
  email: string;
  role: 'admin' | 'user' | 'manager' | 'viewer';
  status: 'active' | 'inactive' | 'pending' | 'suspended';
  department: string;
  joinDate: Date;
  lastLogin: Date;
  tasksCompleted: number;
  performance: number; // 0-100
}

const departments = [
  'Engineering',
  'Marketing',
  'Sales',
  'Support',
  'HR',
  'Finance',
  'Operations',
  'Product',
];

const firstNames = [
  'John',
  'Jane',
  'Michael',
  'Sarah',
  'David',
  'Emma',
  'James',
  'Emily',
  'Robert',
  'Lisa',
  'William',
  'Maria',
  'Richard',
  'Jennifer',
  'Thomas',
  'Linda',
  'Charles',
  'Patricia',
  'Daniel',
  'Barbara',
];

const lastNames = [
  'Smith',
  'Johnson',
  'Williams',
  'Brown',
  'Jones',
  'Garcia',
  'Miller',
  'Davis',
  'Rodriguez',
  'Martinez',
  'Wilson',
  'Anderson',
  'Taylor',
  'Thomas',
  'Moore',
  'Jackson',
  'Martin',
  'Lee',
  'Thompson',
  'White',
];

function generateUser(index: number): User {
  const firstName = firstNames[Math.floor(Math.random() * firstNames.length)];
  const lastName = lastNames[Math.floor(Math.random() * lastNames.length)];
  const name = `${firstName} ${lastName}`;
  const email = `${firstName.toLowerCase()}.${lastName.toLowerCase()}@company.com`;

  const roles: User['role'][] = ['admin', 'user', 'manager', 'viewer'];
  const statuses: User['status'][] = ['active', 'inactive', 'pending', 'suspended'];

  // More active users than inactive
  const statusWeights = [0.7, 0.15, 0.1, 0.05]; // active, inactive, pending, suspended
  const random = Math.random();
  let statusIndex = 0;
  let cumulative = 0;
  for (let i = 0; i < statusWeights.length; i++) {
    cumulative += statusWeights[i];
    if (random < cumulative) {
      statusIndex = i;
      break;
    }
  }

  const joinDate = new Date();
  joinDate.setDate(joinDate.getDate() - Math.floor(Math.random() * 730)); // Random date within last 2 years

  const lastLogin = new Date();
  lastLogin.setDate(lastLogin.getDate() - Math.floor(Math.random() * 30)); // Random date within last 30 days

  return {
    id: `user-${index + 1}`,
    name,
    email,
    role: roles[Math.floor(Math.random() * roles.length)],
    status: statuses[statusIndex],
    department: departments[Math.floor(Math.random() * departments.length)],
    joinDate,
    lastLogin,
    tasksCompleted: Math.floor(Math.random() * 500),
    performance: Math.floor(Math.random() * 40) + 60, // 60-100
  };
}

export const mockUsers: User[] = Array.from({ length: 150 }, (_, i) => generateUser(i));

// ============================================================================
// Transaction Data
// ============================================================================

export interface Transaction {
  id: string;
  date: Date;
  customer: string;
  amount: number;
  status: 'completed' | 'pending' | 'failed' | 'refunded';
  category: string;
  paymentMethod: string;
  description: string;
}

const categories = [
  'Software',
  'Hardware',
  'Services',
  'Consulting',
  'Training',
  'Support',
  'Subscription',
  'License',
];

const paymentMethods = ['Credit Card', 'PayPal', 'Bank Transfer', 'Invoice', 'Crypto'];

const customerCompanies = [
  'Acme Corp',
  'Globex Inc',
  'Initech LLC',
  'Umbrella Corp',
  'Wonka Industries',
  'Stark Enterprises',
  'Wayne Enterprises',
  'Oscorp',
  'Cyberdyne Systems',
  'Massive Dynamic',
];

function generateTransaction(index: number): Transaction {
  const statuses: Transaction['status'][] = ['completed', 'pending', 'failed', 'refunded'];
  const statusWeights = [0.75, 0.15, 0.07, 0.03];
  const random = Math.random();
  let statusIndex = 0;
  let cumulative = 0;
  for (let i = 0; i < statusWeights.length; i++) {
    cumulative += statusWeights[i];
    if (random < cumulative) {
      statusIndex = i;
      break;
    }
  }

  const date = new Date();
  date.setDate(date.getDate() - Math.floor(Math.random() * 180)); // Random date within last 6 months

  const category = categories[Math.floor(Math.random() * categories.length)];

  return {
    id: `txn-${String(index + 1).padStart(6, '0')}`,
    date,
    customer: customerCompanies[Math.floor(Math.random() * customerCompanies.length)],
    amount: Math.floor(Math.random() * 100000) + 100, // $100 - $100,100
    status: statuses[statusIndex],
    category,
    paymentMethod: paymentMethods[Math.floor(Math.random() * paymentMethods.length)],
    description: `${category} purchase`,
  };
}

export const mockTransactions: Transaction[] = Array.from({ length: 200 }, (_, i) =>
  generateTransaction(i)
);

// ============================================================================
// Product Inventory Data
// ============================================================================

export interface Product {
  id: string;
  sku: string;
  name: string;
  category: string;
  stock: number;
  lowStockThreshold: number;
  price: number;
  supplier: string;
  lastRestocked: Date;
  status: 'in-stock' | 'low-stock' | 'out-of-stock';
}

const productNames = [
  'Wireless Mouse',
  'Mechanical Keyboard',
  'USB-C Cable',
  'HDMI Adapter',
  'Laptop Stand',
  'Monitor Arm',
  'Webcam',
  'Microphone',
  'Headphones',
  'Speakers',
  'External SSD',
  'Power Bank',
  'Docking Station',
  'Laptop Bag',
  'Cable Organizer',
  'Phone Stand',
  'Tablet Case',
  'Screen Protector',
  'Cleaning Kit',
  'Desk Mat',
];

const productCategories = [
  'Accessories',
  'Peripherals',
  'Storage',
  'Audio',
  'Cables',
  'Furniture',
  'Cases',
  'Maintenance',
];

const suppliers = [
  'TechSupply Co',
  'Global Electronics',
  'Premium Parts Ltd',
  'QuickShip Inc',
  'Quality Goods LLC',
];

function generateProduct(index: number): Product {
  const name = productNames[index % productNames.length];
  const stock = Math.floor(Math.random() * 500);
  const lowStockThreshold = 50;

  let status: Product['status'] = 'in-stock';
  if (stock === 0) {
    status = 'out-of-stock';
  } else if (stock < lowStockThreshold) {
    status = 'low-stock';
  }

  const lastRestocked = new Date();
  lastRestocked.setDate(lastRestocked.getDate() - Math.floor(Math.random() * 90)); // Random date within last 90 days

  return {
    id: `prod-${index + 1}`,
    sku: `SKU-${String(index + 1).padStart(5, '0')}`,
    name,
    category: productCategories[Math.floor(Math.random() * productCategories.length)],
    stock,
    lowStockThreshold,
    price: Math.floor(Math.random() * 500) + 10, // $10 - $510
    supplier: suppliers[Math.floor(Math.random() * suppliers.length)],
    lastRestocked,
    status,
  };
}

export const mockProducts: Product[] = Array.from({ length: 100 }, (_, i) => generateProduct(i));

// ============================================================================
// Task Data
// ============================================================================

export interface Task {
  id: string;
  title: string;
  assignee: string;
  priority: 'low' | 'medium' | 'high' | 'critical';
  status: 'todo' | 'in-progress' | 'review' | 'done';
  dueDate: Date;
  createdDate: Date;
  project: string;
  estimatedHours: number;
  completionPercentage: number;
}

const taskTitles = [
  'Implement user authentication',
  'Fix navigation bug',
  'Design dashboard mockups',
  'Write API documentation',
  'Update database schema',
  'Optimize query performance',
  'Add unit tests',
  'Refactor component library',
  'Set up CI/CD pipeline',
  'Conduct security audit',
  'Migrate to new framework',
  'Improve error handling',
  'Create onboarding flow',
  'Integrate payment gateway',
  'Build reporting feature',
];

const projects = [
  'Website Redesign',
  'Mobile App',
  'API V2',
  'Admin Dashboard',
  'Customer Portal',
  'Analytics Platform',
];

function generateTask(index: number): Task {
  const priorities: Task['priority'][] = ['low', 'medium', 'high', 'critical'];
  const statuses: Task['status'][] = ['todo', 'in-progress', 'review', 'done'];

  const createdDate = new Date();
  createdDate.setDate(createdDate.getDate() - Math.floor(Math.random() * 60));

  const dueDate = new Date(createdDate);
  dueDate.setDate(dueDate.getDate() + Math.floor(Math.random() * 30) + 7); // 7-37 days after creation

  const status = statuses[Math.floor(Math.random() * statuses.length)];
  const completionPercentage =
    status === 'done'
      ? 100
      : status === 'review'
        ? 80 + Math.floor(Math.random() * 20)
        : status === 'in-progress'
          ? 20 + Math.floor(Math.random() * 60)
          : 0;

  const firstName = firstNames[Math.floor(Math.random() * firstNames.length)];
  const lastName = lastNames[Math.floor(Math.random() * lastNames.length)];

  return {
    id: `task-${index + 1}`,
    title: taskTitles[index % taskTitles.length],
    assignee: `${firstName} ${lastName}`,
    priority: priorities[Math.floor(Math.random() * priorities.length)],
    status,
    dueDate,
    createdDate,
    project: projects[Math.floor(Math.random() * projects.length)],
    estimatedHours: Math.floor(Math.random() * 40) + 4, // 4-44 hours
    completionPercentage,
  };
}

export const mockTasks: Task[] = Array.from({ length: 120 }, (_, i) => generateTask(i));
